import React, { useEffect, useMemo, useState } from 'react'
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  InputAdornment,
  Divider,
  IconButton,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { useDispatch, useSelector } from 'react-redux'
import { track } from '@amplitude/analytics-browser'
import { CoreTextField } from 'src/components/core'
import {
  getTagListAction,
  createTagAction,
  deleteTagAction,
} from 'src/reducers/tag/action'
import { setCollectionTagsAction } from 'src/reducers/collection/action'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import TagVisibilityBadge from 'src/components/tag-visibility-badge'
import CreateTagDialog from '../create-tag-dialog'

const TagPopover = ({ anchorEl, open, onClose, collection }) => {
  const dispatch = useDispatch()
  const allTags = useSelector(state => state.tag.list)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (open && allTags.length === 0) {
      dispatch(getTagListAction())
    }
  }, [open, allTags.length, dispatch])

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const assignedIds = useMemo(
    () => new Set((collection?.tags || []).map(t => t.id)),
    [collection],
  )

  const filteredTags = useMemo(() => {
    if (!search.trim()) return allTags
    const q = search.trim().toLowerCase()
    return allTags.filter(t => (t.name || '').toLowerCase().includes(q))
  }, [allTags, search])

  const persist = nextIds => {
    const resolved = nextIds
      .map(id => allTags.find(t => t.id === id))
      .filter(Boolean)
    dispatch(setCollectionTagsAction(collection.id, nextIds, resolved))
  }

  const toggleTag = tag => {
    const isAssigned = assignedIds.has(tag.id)
    track(isAssigned ? 'collection_tag_removed' : 'collection_tag_assigned', {
      collection_id: collection?.id,
      collection_name: collection?.name,
      tag_id: tag.id,
      tag_name: tag.name,
    })
    const next = isAssigned
      ? Array.from(assignedIds).filter(id => id !== tag.id)
      : [...assignedIds, tag.id]
    persist(next)
  }

  const requestDeleteTag = tag => {
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          variant: MessageBoxVariant.NOTICE,
          title: 'Delete tag',
          subTitle: `Delete "${tag.name}"?`,
          description:
            'This tag will be removed from every learning path it was assigned to.',
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => dispatch(messageBoxHideAction()),
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Delete',
              ButtonActionType.DELETE,
              () => {
                track('tag_deleted', {
                  tag_id: tag.id,
                  tag_name: tag.name,
                })
                dispatch(
                  deleteTagAction(tag.id, () => {
                    dispatch(messageBoxHideAction())
                  }),
                )
              },
            ),
          ],
        }),
      ),
    )
  }

  const openCreateDialog = () => {
    // The dialog renders on top of this popover, so leaving it open stacks two overlapping
    // surfaces. The new tag is assigned through the callback below, which does not need the
    // popover to still be mounted.
    onClose()

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'Create New Tag',
          draggable: false,
          maxWidth: '420px',
          component: (
            <CreateTagDialog
              onCreated={tag => {
                if (!tag) return
                const next = [...assignedIds, tag.id]
                const resolved = [...(collection?.tags || []), tag]
                dispatch(setCollectionTagsAction(collection.id, next, resolved))
              }}
            />
          ),
        }),
      ),
    )
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: { width: 280, p: '12px', borderRadius: '12px' },
      }}
      onClick={e => e.stopPropagation()}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '8px',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Tag{' '}
          <Typography
            component="span"
            sx={{ fontWeight: 400, fontSize: '14px' }}
          >
            {collection?.name}
          </Typography>
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Icon icon="mdi:close" width={16} height={16} />
        </IconButton>
      </Box>

      <CoreTextField
        marginTop={false}
        size="small"
        fullWidth
        placeholder="Search tags"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon icon="mdi:search" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ maxHeight: 220, overflowY: 'auto', mt: '8px' }}>
        {filteredTags.map(tag => (
          <Box
            key={tag.id}
            onClick={() => toggleTag(tag)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              p: '4px',
              borderRadius: '6px',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f3f5f9' },
              '&:hover .tag-row-delete': { opacity: 1 },
            }}
          >
            <Checkbox
              checked={assignedIds.has(tag.id)}
              size="small"
              onClick={e => {
                e.stopPropagation()
                toggleTag(tag)
              }}
              sx={{ p: 0 }}
            />
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: tag.color,
                display: 'inline-block',
              }}
            />
            <Typography sx={{ fontSize: '14px', flex: 1 }}>
              {tag.name}
            </Typography>
            <TagVisibilityBadge isPublic={!!tag.isPublic} />
            <IconButton
              size="small"
              className="tag-row-delete"
              onClick={e => {
                e.stopPropagation()
                requestDeleteTag(tag)
              }}
              sx={{
                opacity: 0,
                color: '#d4351c',
                p: '2px',
                transition: 'opacity 0.15s',
              }}
            >
              <Icon icon="mdi:trash-can-outline" width={16} height={16} />
            </IconButton>
          </Box>
        ))}
        {filteredTags.length === 0 && (
          <Typography
            sx={{
              fontSize: '13px',
              color: '#9aa3b2',
              textAlign: 'center',
              py: '12px',
            }}
          >
            {search ? 'No tags match your search.' : 'No tags yet.'}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: '8px' }} />

      <Box
        role="button"
        onClick={openCreateDialog}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          p: '4px',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          '&:hover': { backgroundColor: '#f3f5f9' },
        }}
      >
        Create new tag
      </Box>
    </Popover>
  )
}

export default TagPopover
